/**
 * The audio's numbers and the pure mappings from the game to the sound (GDD §9): volume curve, engine pitch and
 * loudness from the jet's speed, Doppler, explosion shapes, the low-fuel alarm's rate, the refuel tone's pitch and
 * the music's intensity. No Web Audio here, so all of it is unit-tested in Node; functions that return several
 * numbers write them into a preallocated record (GDD §10.5).
 */
import { config } from '../core/config.ts';

/** Speed of sound in air at 20 °C (m/s), for Doppler (judgment call 44). */
export const SPEED_OF_SOUND = 343;

export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export function smoothstep(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

/** A volume slider (0–1) as a gain: the square, so the slider's middle sounds about half as loud (judgment call 50). */
export function sliderGain(v: number): number {
  const c = clamp01(v);
  return c * c;
}

/** MIDI note number to frequency (A4 = 69 = 440 Hz). */
export function hz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** The jet engine's layers at a speed (GDD §9: pitch and volume follow the throttle; afterburner roar on boost). */
export interface EngineTone {
  /** Low turbine body: two detuned saws through a low-pass. */
  rumbleHz: number;
  rumbleGain: number;
  cutoffHz: number;
  /** Compressor whine. */
  whineHz: number;
  whineGain: number;
  /** Air rushing past: band-passed noise. */
  airHz: number;
  airGain: number;
  /** Afterburner roar, 0–1: silent at cruise and below, full at top speed. */
  burner: number;
}

export function createEngineTone(): EngineTone {
  return {
    rumbleHz: 0.5,
    rumbleGain: 0.5,
    cutoffHz: 0.5,
    whineHz: 0.5,
    whineGain: 0.5,
    airHz: 0.5,
    airGain: 0.5,
    burner: 0.5,
  };
}

export function engineTone(speed: number, out: EngineTone): EngineTone {
  const f = config.flight;
  const t = clamp01((speed - f.min) / (f.max - f.min));
  out.rumbleHz = 46 + 26 * t;
  out.rumbleGain = 0.2 + 0.1 * t;
  out.cutoffHz = 320 + 820 * t;
  out.whineHz = 780 + 620 * t;
  out.whineGain = 0.018 + 0.03 * t;
  out.airHz = 650 + 950 * t;
  out.airGain = 0.08 + 0.14 * t;
  out.burner = smoothstep(f.cruise + 2, f.max, speed);
  return out;
}

/**
 * Doppler factor for a source at `r` from the listener (source minus listener), moving at `sv`, heard by a listener
 * moving at `lv` (m/s): (c + listener speed toward the source) / (c + source speed away from the listener).
 * Above 1 while they close, below 1 while they part; clamped to 0.5–2.
 */
export function doppler(
  rx: number,
  ry: number,
  rz: number,
  svx: number,
  svy: number,
  svz: number,
  lvx: number,
  lvy: number,
  lvz: number,
): number {
  const d = Math.sqrt(rx * rx + ry * ry + rz * rz);
  if (d < 1e-6) return 1;
  const toward = (lvx * rx + lvy * ry + lvz * rz) / d;
  const away = (svx * rx + svy * ry + svz * rz) / d;
  const den = SPEED_OF_SOUND + away;
  // A source closing faster than sound would break the formula: it is simply as high as the clamp allows.
  if (den <= 0) return 2;
  const f = (SPEED_OF_SOUND + toward) / den;
  return f < 0.5 ? 0.5 : f > 2 ? 2 : f;
}

/** An explosion's sound (GDD §9: sized to the blast, with a low-end boom, debris and a water splash). */
export interface BoomShape {
  gain: number;
  /** Length of the boom's tail (s). */
  secs: number;
  /** The boom's sine falls from startHz to endHz. */
  startHz: number;
  endHz: number;
  /** Low-pass on the rumble of noise under it. */
  bodyHz: number;
  /** Debris crackle level (0 = none). */
  debris: number;
  /** Splash level (0 = not over water). */
  splash: number;
}

export function createBoomShape(): BoomShape {
  return { gain: 0.5, secs: 0.5, startHz: 0.5, endHz: 0.5, bodyHz: 0.5, debris: 0.5, splash: 0.5 };
}

/** Blast sizes run from 0.7 (a bridge turret) to 2.2 (a bridge); boats 0.9, enemy jets 1.2, barges 1.6, the jet 1.8. */
export function boomShape(size: number, overWater: boolean, out: BoomShape): BoomShape {
  const k = clamp01((size - 0.7) / 1.5);
  out.gain = 0.5 + 0.4 * k;
  out.secs = 0.7 + 1.1 * k;
  out.startHz = 120 - 45 * k;
  out.endHz = 34 - 6 * k;
  out.bodyHz = 900 - 500 * k;
  out.debris = 0.25 + 0.2 * k;
  out.splash = overWater ? 0.22 + 0.25 * k : 0;
  return out;
}

/** An explosion this low is over the water: boats and barges sit at 0 m; the jet flies at 12 m. */
export const WATER_HEIGHT = 4;

/** Seconds between low-fuel alarm figures: 0.7 s at the warning threshold, 0.34 s with the tank empty (call 51). */
export function alarmPeriod(fuel: number): number {
  return 0.34 + 0.36 * clamp01(fuel / config.fuel.lowThreshold);
}

/** The refuel blip's pitch: climbs an octave, 440 → 880 Hz, as the tank fills (judgment call 51). */
export function refuelHz(fuel: number): number {
  return 440 * Math.pow(2, clamp01(fuel));
}

/** Seconds between refuel blips. */
export const REFUEL_PERIOD = 0.14;

/** What raises the music (GDD §9: open water calm; intensity rises with the level, low fuel and incoming fire). */
export function musicTarget(
  playing: boolean,
  level: number,
  near: number,
  incoming: number,
  lowFuel: boolean,
  engaged: boolean,
): number {
  if (!playing) return 0.08;
  const base = 0.12 + 0.05 * Math.min(Math.max(level - 1, 0), 9);
  return clamp01(base + 0.22 * clamp01(near) + 0.25 * clamp01(incoming) + (lowFuel ? 0.2 : 0) + (engaged ? 0.15 : 0));
}

/** Seconds the intensity takes to rise (time constant) and to fall back. */
export const INTENSITY_RISE = 0.5;
export const INTENSITY_FALL = 3;

/** Intensity follows its target: quickly up, slowly down, so a fight lingers and open water settles. */
export function followIntensity(current: number, target: number, dt: number): number {
  const tc = target > current ? INTENSITY_RISE : INTENSITY_FALL;
  return current + (target - current) * (1 - Math.exp(-dt / tc));
}

/** Enemies this far ahead (m) count toward the music's intensity; threats behind the jet don't. */
export const NEAR_AHEAD = 220;
export const NEAR_BEHIND = 20;
/** Three threats close ahead make full "near"; three shots in flight make full "incoming". */
export const NEAR_FULL = 3;
export const INCOMING_FULL = 3;

/**
 * The `k` smallest of `dist[0..n)` that are at most `max`, nearest first, as indices into `out`. Returns how many.
 * Insertion into a short list: k is at most 3 here.
 */
export function nearest(dist: Float64Array, n: number, k: number, max: number, out: Int32Array): number {
  let m = 0;
  for (let i = 0; i < n; i++) {
    const d = dist[i] ?? Infinity;
    if (!(d <= max)) continue;
    let j = m < k ? m++ : k;
    if (j === k && d >= (dist[out[k - 1] ?? 0] ?? Infinity)) continue;
    if (j === k) j = k - 1;
    while (j > 0 && (dist[out[j - 1] ?? 0] ?? Infinity) > d) {
      out[j] = out[j - 1] ?? 0;
      j--;
    }
    out[j] = i;
  }
  return m;
}

/** How far each continuous enemy sound carries (m). */
export const LOOP_RANGE = { heli: 240, ship: 200, jet: 320 } as const;
